import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Pencil } from 'lucide-react'
import { toast } from 'sonner'
import { useOrganization } from '../contexts/OrganizationContext'
import { tagApi } from '../services/api'
import TagForm from './TagForm'
import { EMPTY_TAG } from './tagUtils'
import { TagChipContent } from './post-creator/tagChipContent'
import SuggestedTagChips from './post-creator/SuggestedTagChips'
import styles from './TagSelector.module.css'

function TagSelector({
  selectedTagIds = [],
  onChange,
  suggestedTags = [],
  suggestionsEnabled = false,
  suggestionsPending = false,
}) {
  const { currentOrg } = useOrganization()
  const queryClient = useQueryClient()
  const [showCreate, setShowCreate] = useState(false)
  const [editingTag, setEditingTag] = useState(null)
  const [editInitialData, setEditInitialData] = useState(EMPTY_TAG)

  const { data, isLoading } = useQuery({
    queryKey: ['tags', currentOrg?.slug],
    queryFn: () => tagApi.list(currentOrg.slug, true),
    enabled: !!currentOrg?.slug,
  })

  const tags = data?.tags || []
  // A cheap presence check; SuggestedTagChips does the one normalisation.
  const hasSuggestions = Array.isArray(suggestedTags) && suggestedTags.some((suggestion) => String(suggestion || '').trim())

  const toggleTag = (tagId) => {
    const next = selectedTagIds.includes(tagId)
      ? selectedTagIds.filter((id) => id !== tagId)
      : [...selectedTagIds, tagId]
    onChange(next)
  }

  const handleCreate = async (formData) => {
    const created = await tagApi.create(currentOrg.slug, formData)
    queryClient.invalidateQueries({ queryKey: ['tags', currentOrg?.slug] })
    onChange([...selectedTagIds, created.id])
    toast.success('Tag created')
  }

  const openEdit = (tag) => {
    setEditInitialData({
      label: tag.label,
      bg_color: tag.bg_color,
      text_color: tag.text_color,
      icon: tag.icon || '',
      font_style: tag.font_style,
      position: tag.position,
      sort_order: tag.sort_order,
    })
    setEditingTag(tag.id)
  }

  const handleEdit = async (formData) => {
    await tagApi.update(currentOrg.slug, editingTag, formData)
    queryClient.invalidateQueries({ queryKey: ['tags', currentOrg?.slug] })
    toast.success('Tag updated')
  }

  if (isLoading) return <div className={styles.loading}>Loading tags...</div>

  return (
    <div className={styles.container}>
      {/* Not a field label: these are toggle buttons, so the caption names a
          group rather than labelling a single control. */}
      <span className={styles.label} id="tag-selector-label">Tags</span>
      <div className={styles.tagList} role="group" aria-labelledby="tag-selector-label">
        {tags.map((tag) => (
          <div key={tag.id} className={styles.tagItem}>
            <button
              type="button"
              className={`${styles.tagChip} ${selectedTagIds.includes(tag.id) ? styles.selected : ''}`}
              onClick={() => toggleTag(tag.id)}
              aria-pressed={selectedTagIds.includes(tag.id)}
            >
              <TagChipContent tag={tag} />
            </button>
            <button
              type="button"
              className={styles.editBtn}
              onClick={() => openEdit(tag)}
              aria-label={`Edit ${tag.label}`}
              title={`Edit ${tag.label}`}
            >
              <Pencil size={13} aria-hidden="true" />
            </button>
          </div>
        ))}

        <button
          type="button"
          className={styles.addBtn}
          onClick={() => setShowCreate(true)}
        >
          + New Tag
        </button>
      </div>
      {suggestionsEnabled || hasSuggestions ? (
        <SuggestedTagChips
          suggestions={suggestedTags}
          tags={tags}
          selectedTagIds={selectedTagIds}
          onToggleTag={toggleTag}
          pending={suggestionsPending}
          emptyMessage="Suggestions appear after you finish writing the post text."
        />
      ) : null}


      {showCreate && (
        <TagForm
          title="Create Tag"
          saveLabel="Create Tag"
          onSave={handleCreate}
          onClose={() => setShowCreate(false)}
        />
      )}

      {editingTag && (
        <TagForm
          initialData={editInitialData}
          title="Edit Tag"
          saveLabel="Save Tag"
          onSave={handleEdit}
          onClose={() => setEditingTag(null)}
        />
      )}
    </div>
  )
}

export default TagSelector
